import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { formatarData } from "../lib/dados";
import { lerMeuAcervo, urlDoAnexo, type DocumentoDoAcervo } from "../lib/faculdade";
import { carregarMeuPerfil } from "../lib/perfil";
import { Aviso, Carregando, Topo } from "../componentes/ui";
import Rodape from "../componentes/Rodape";

// Meus documentos — o ACERVO do aluno (requisito 3.5, 000281).
//
// ── Por que uma porta SÓ, e não duas ───────────────────────────────────────
//
// A proposta que chegou tinha duas telas: uma dedicada para quem não tem mais
// acesso nenhum, e o acervo dentro do Perfil para quem ainda estuda outro
// curso. Duas telas para o mesmo acervo é a cicatriz dos dois catálogos de
// CNAE — elas divergem, e a que divergir em silêncio é a que ninguém olha.
//
// Então o endereço é UM e é sempre o mesmo, alcançável pelo menu da conta por
// qualquer aluno. O que muda entre o formado e o matriculado não é a tela: é o
// que existe em volta dela.
//
// ── O que esta tela NÃO mostra, de propósito ───────────────────────────────
//
// O que o ALUNO enviou (o diploma da graduação, o RG, o comprovante) fica na
// Secretaria, onde ele é assunto de análise. Aqui é só o que a CASA entregou —
// e um pedido em andamento não entra, porque ofereceria um download que ainda
// não existe (a cicatriz da 000154, em que o número do certificado aparecia
// antes de a secretaria liberá-lo).
export default function MeusDocumentos() {
  const [docs, setDocs] = useState<DocumentoDoAcervo[] | null>(null);
  const [eu, setEu] = useState<{ nome: string | null; avatar: string | null }>({
    nome: null,
    avatar: null,
  });
  const [estado, setEstado] = useState<"carregando" | "pronto" | "erro">("carregando");

  useEffect(() => {
    let ativo = true;
    (async () => {
      try {
        // Duas chamadas em paralelo, e não o `useHub`: ele faz sete (cursos,
        // produtos, matrículas, contratos, vitrine, seções…) para uma tela que
        // precisa do acervo e do nome no Topo. O perfil é a MESMA leitura que a
        // tela de Perfil usa.
        const [lista, perfil] = await Promise.all([lerMeuAcervo(), carregarMeuPerfil()]);
        if (!ativo) return;
        setDocs(lista);
        setEu({ nome: perfil?.nome ?? null, avatar: perfil?.avatar_url ?? null });
        setEstado("pronto");
      } catch {
        if (ativo) setEstado("erro");
      }
    })();
    return () => {
      ativo = false;
    };
  }, []);

  if (estado === "carregando") return <Carregando texto="Abrindo os seus documentos…" />;

  if (estado === "erro" || !docs) {
    return (
      <Aviso titulo="Algo deu errado" acao={<Link to="/" className="btn-contorno">Voltar ao início</Link>}>
        Não conseguimos abrir os seus documentos agora. Recarregue a página; se continuar, fale com o
        suporte que a gente resolve.
      </Aviso>
    );
  }

  // Agrupado por CURSO, que é como o aluno procura ("o certificado da minha
  // pós"), e não por tipo de documento. O documento sem curso (12 em produção)
  // cai num grupo próprio no fim.
  const grupos = agruparPorCurso(docs);
  const vias = numerarVias(docs);

  return (
    <div className="min-h-screen">
      <Topo atual="inicio" nome={eu.nome} avatarUrl={eu.avatar} />

      <div className="mx-auto max-w-4xl px-4 pt-10 pb-16 sm:px-6">
        <h1 className="font-display text-3xl font-bold text-titulo">Meus documentos</h1>
        <p className="mt-2 max-w-2xl text-cinza">
          Tudo que a Faculdade já emitiu para você. Estes documentos continuam aqui depois que o
          curso termina — você não precisa pedir segunda via à secretaria.
        </p>

        {docs.length === 0 ? (
          <div className="surface mt-8 p-6">
            <h2 className="font-display text-lg font-bold text-titulo">
              Você ainda não tem documentos emitidos
            </h2>
            <p className="mt-2 text-sm text-cinza">
              Quando a secretaria emitir o seu certificado ou o informe de rendimentos, ele aparece
              aqui — e fica. Para pedir um documento, use a Secretaria do Aluno na área da
              Faculdade.
            </p>
            <Link to="/faculdade/secretaria" className="btn-contorno mt-4 inline-flex">
              Ir para a Secretaria
            </Link>
          </div>
        ) : (
          <div className="mt-8 flex flex-col gap-6">
            {grupos.map((g) => (
              <section
                key={g.chave}
                role="group"
                aria-labelledby={`grupo-${g.chave}`}
                className="surface p-5"
              >
                {/* <section role="group"> em vez de <fieldset><legend>: a
                    especificação posiciona o legend SOBRE a borda, e com o card
                    arredondado o título sai flutuando fora da caixa. */}
                <h2 id={`grupo-${g.chave}`} className="font-display text-lg font-bold text-titulo">
                  {g.nome}
                </h2>
                <ul className="mt-3 divide-y divide-borda">
                  {g.docs.map((d) => (
                    <LinhaDoDocumento key={d.id} doc={d} via={vias.get(d.id) ?? null} />
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}

        <Rodape />
      </div>
    </div>
  );
}

function LinhaDoDocumento({ doc, via }: { doc: DocumentoDoAcervo; via: number | null }) {
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 py-3">
      <div className="min-w-0">
        {/* O nome em escopo PRÓPRIO (`data-doc-nome`): o `textContent` de um
            <p> inclui tudo que está dentro dele, e o selo da via colava no nome
            ("Declaração de matrícula2ª via") para qualquer leitor — a prova e o
            leitor de tela. É a cicatriz do rótulo com selo dentro, e o lugar de
            resolver é aqui, não afrouxando a asserção. */}
        <p className="font-medium text-tinta">
          {/* O ANO entra no NOME, e não num selo ao lado: "Informe de
              rendimentos 2025" é o documento inteiro, enquanto o selo diria que
              há um documento com uma propriedade. Os dois anos são documentos
              DIFERENTES — ao contrário das vias, que são cópias do mesmo. */}
          <span data-doc-nome>
            {doc.nome}
            {doc.competencia_ano ? ` ${doc.competencia_ano}` : ""}
          </span>
          {/* O selo da VIA só aparece onde há mais de uma — medido em 22/09:
              1.387 slots (aluno, tipo, curso) têm repetição, somando 3.126
              documentos, e o maior tem QUINZE. Sem ele, a tela mostra duas
              linhas idênticas e o aluno não sabe qual clicar (a cicatriz dos 31
              fornecedores do Split, que eram 19 repetidos por vigência).
              `nome_origem` não serviria: as variações dele são de CAIXA
              ("Declaração de Matrícula"), não de conteúdo. */}
          {via !== null && (
            <span className="ml-2 rounded-full bg-chip px-2 py-0.5 text-xs font-semibold text-cinza">
              {via}ª via
            </span>
          )}
        </p>
        <p className="text-sm text-cinza">{procedencia(doc)}</p>
      </div>
      <BaixarDocumento doc={doc} />
    </li>
  );
}

/**
 * A data, dizendo o que ela É.
 *
 * `entregue_em` existe em 6 dos 15.337 entregues — a carga do Bubble não trouxe
 * a data da entrega. Onde ela falta, a tela mostra a data do PEDIDO e a chama
 * assim. Chamar uma de outra seria afirmar o que não se sabe, que é a lição da
 * 000278 sobre a data de colação: o documento tem três datas e só se estampa a
 * que ele nomeia.
 */
function procedencia(doc: DocumentoDoAcervo): string {
  if (doc.entregue_em) return `Entregue em ${formatarData(doc.entregue_em)}`;
  return `Pedido em ${formatarData(doc.criado_em)}`;
}

/**
 * O bucket é PRIVADO: a url é assinada e expira em 5 minutos.
 *
 * Este botão é irmão do `BotaoDeAnexo` da Secretaria e não o reusa por uma
 * razão de cor: lá a tela inteira é o território navy da Faculdade e o link é
 * `laranja-claro`; aqui o fundo é claro, e `laranja-claro` sobre branco
 * reprova. `link` troca com o tema, que é o que este papel pede.
 */
function BaixarDocumento({ doc }: { doc: DocumentoDoAcervo }) {
  const [abrindo, setAbrindo] = useState(false);
  const [erro, setErro] = useState(false);

  if (doc.anexo_path) {
    return (
      <div className="text-right">
        <button
          type="button"
          disabled={abrindo}
          onClick={async () => {
            setAbrindo(true);
            setErro(false);
            const url = await urlDoAnexo(doc.anexo_path!);
            setAbrindo(false);
            if (url) window.open(url, "_blank", "noopener,noreferrer");
            else setErro(true);
          }}
          className="btn-contorno text-sm disabled:opacity-60"
        >
          {abrindo ? "Abrindo…" : "Baixar"}
        </button>
        {/* Erro de UM download fica NA LINHA: trocar a tela inteira por uma
            mensagem apagaria a lista que a pessoa está lendo. */}
        {erro && <p className="mt-1 text-xs text-erro-tinta">Não abriu. Tente de novo.</p>}
      </div>
    );
  }

  if (!doc.anexo_externo_url) return null;

  return (
    <a href={doc.anexo_externo_url} target="_blank" rel="noreferrer" className="btn-contorno text-sm">
      Baixar
      <span className="sr-only"> (abre em outra aba)</span>
    </a>
  );
}

/**
 * Numera as vias de cada (tipo, curso) que tem MAIS DE UMA.
 *
 * A 1ª via é a mais ANTIGA, que é como a palavra é usada — "pedi a segunda
 * via". O banco devolve do mais recente para o mais antigo, então a contagem
 * anda de trás para a frente.
 *
 * Quem tem uma via só fica SEM selo: "1ª via" ao lado de um documento único não
 * informa nada, e um marcador que aparece em todas as linhas é ruído (a
 * cicatriz do selo "sem dono" que apareceu 27 vezes na Visão do Catálogo).
 */
function numerarVias(docs: DocumentoDoAcervo[]): Map<string, number> {
  const porSlot = new Map<string, DocumentoDoAcervo[]>();
  for (const d of docs) {
    // A competência entra na chave: o informe de 2025 não é uma VIA do de 2026,
    // e chamá-lo assim inverteria o sentido da palavra — via é cópia do mesmo
    // documento, e estes são documentos diferentes.
    const chave = `${d.tipo_id ?? d.nome}|${d.curso_slug ?? ""}|${d.competencia_ano ?? ""}`;
    const lista = porSlot.get(chave);
    if (lista) lista.push(d);
    else porSlot.set(chave, [d]);
  }
  const vias = new Map<string, number>();
  for (const lista of porSlot.values()) {
    if (lista.length < 2) continue;
    lista.forEach((d, i) => vias.set(d.id, lista.length - i));
  }
  return vias;
}

type Grupo = { chave: string; nome: string; docs: DocumentoDoAcervo[] };

function agruparPorCurso(docs: DocumentoDoAcervo[]): Grupo[] {
  const mapa = new Map<string, Grupo>();
  for (const d of docs) {
    const chave = d.curso_slug ?? "sem-curso";
    let g = mapa.get(chave);
    if (!g) {
      g = { chave, nome: d.curso_nome ?? d.curso_slug ?? "Outros documentos", docs: [] };
      mapa.set(chave, g);
    }
    g.docs.push(d);
  }
  // A ordem dentro de cada grupo é a que o banco deu (mais recente primeiro) —
  // o `Map` preserva a ordem de inserção, então o grupo do documento mais novo
  // vem primeiro. O grupo sem curso vai para o fim.
  return [...mapa.values()].sort((a, b) => Number(a.chave === "sem-curso") - Number(b.chave === "sem-curso"));
}
